// MARK://dhk8/admin-shell/lib/mock-data
// Deterministic seed data for the no-backend preview build (VITE_API_STUB=1).
// Each client in lib/clients/* short-circuits to these when PREVIEW_STUB is on, so the
// whole admin clicks through with content and no services running.

export interface Category { id: string; name: string; sortOrder: number; active: boolean; stationId?: string | null; publicVisible: boolean }
export interface Product { id: string; name: string; sku: string; priceCents: number; categoryId: string; available: boolean; taxRate: string; description?: string; unit?: string; imageUrl?: string | null; imageKey?: string | null; type?: 'PRODUCT' | 'SERVICE' | 'COMBO' | 'INGREDIENT'; tags?: string[]; tracksInventory?: boolean; consumesRecipe?: boolean; satProductKey?: string; satUnitKey?: string }
export interface Variant { id: string; productId: string; name: string; priceDeltaCents: number }
export interface Modifier { id: string; groupName: string; name: string; priceCents: number; required: boolean }
export interface Combo { id: string; name: string; priceCents: number; itemIds: string[] }
export interface PriceList { id: string; name: string; active: boolean; overrides: Record<string, number> }

export interface FloorSection { id: string; name: string }
export interface FloorTable { id: string; code: string; capacity: number; posX: number; posY: number; widthPx: number; heightPx: number; shape: 'ROUND' | 'RECT'; sectionId: string }

export interface AdminUser { id: string; fullName: string; email: string; role: string; pinId: string | null; active: boolean }
export interface Printer { id: string; name: string; ip: string; station: string; kind: 'RECEIPT' | 'KITCHEN'; online: boolean }
export interface KdsStation { id: string; name: string; categories: string[] }

export interface FiscalSettings {
  rfc: string; legalName: string; regimeCode: string; regimeName: string
  csdUploaded: boolean; csdExpiresAt: string | null
  folioSeries: string; folioNext: number
  businessHours: { day: string; open: string; close: string }[]
  receiptHeader: string; receiptFooter: string
}

export interface ReportSummary { id: string; name: string; period: string; updatedAt: string }
export interface Invoice { id: string; folio: string; customer: string; rfc: string; totalCents: number; status: 'STAMPED' | 'CANCELED' | 'PENDING'; date: string; uuid: string }
export interface FiscalCustomer { id: string; name: string; rfc: string; email: string; regimeCode: string; usoCfdi: string }
export interface InventoryItem { id: string; name: string; unit: string; onHand: number; reorderLevel: number; status: 'OK' | 'LOW' | 'OUT' }

export interface TenantAccount { id: string; name: string; legalName: string; plan: string; status: 'ACTIVE' | 'SUSPENDED' | 'TRIAL'; mode: 'SANDBOX' | 'LIVE'; createdAt: string; ownerEmail: string }

// Onboarding: ingredient (cost + unit) → recipe (qty per portion) → product + price.
export interface Ingredient { id: string; name: string; unit: string; costCents: number }     // costCents = cost of ONE unit (1 tortilla, 1 kg de carne…)
export interface RecipeLine { ingredientId: string; qtyPerPortion: number }                     // how much of that unit goes in one portion

const $ = (mxn: number) => Math.round(mxn * 100)

export const CATEGORIES: Category[] = [
  { id: 'cat-entradas', name: 'Entradas', sortOrder: 1, active: true, publicVisible: true },
  { id: 'cat-fuertes', name: 'Platos fuertes', sortOrder: 2, active: true, publicVisible: true },
  { id: 'cat-bebidas', name: 'Bebidas', sortOrder: 3, active: true, publicVisible: true },
  { id: 'cat-postres', name: 'Postres', sortOrder: 4, active: true, publicVisible: true },
]
export const PRODUCTS: Product[] = [
  { id: 'p-guac', name: 'Guacamole', sku: 'GUAC', priceCents: $(95), categoryId: 'cat-entradas', available: true, taxRate: '0.16' },
  { id: 'p-quesa', name: 'Quesadillas', sku: 'QUESA', priceCents: $(85), categoryId: 'cat-entradas', available: true, taxRate: '0.16' },
  { id: 'p-tacos', name: 'Tacos al pastor (3)', sku: 'TACOS', priceCents: $(120), categoryId: 'cat-fuertes', available: true, taxRate: '0.16' },
  { id: 'p-mole', name: 'Pollo en mole', sku: 'MOLE', priceCents: $(165), categoryId: 'cat-fuertes', available: true, taxRate: '0.16' },
  { id: 'p-agua', name: 'Agua de jamaica', sku: 'AGUA', priceCents: $(45), categoryId: 'cat-bebidas', available: true, taxRate: '0.16' },
  { id: 'p-refresco', name: 'Refresco', sku: 'REF', priceCents: $(38), categoryId: 'cat-bebidas', available: false, taxRate: '0.16' },
  { id: 'p-flan', name: 'Flan napolitano', sku: 'FLAN', priceCents: $(65), categoryId: 'cat-postres', available: true, taxRate: '0.16' },
]
export const VARIANTS: Variant[] = [
  { id: 'v-tacos-orden', productId: 'p-tacos', name: 'Orden de 5', priceDeltaCents: $(60) },
  { id: 'v-agua-litro', productId: 'p-agua', name: '1 litro', priceDeltaCents: $(35) },
]
export const MODIFIERS: Modifier[] = [
  { id: 'm-extra-queso', groupName: 'Extras', name: 'Extra queso', priceCents: $(15), required: false },
  { id: 'm-sin-cebolla', groupName: 'Quitar', name: 'Sin cebolla', priceCents: 0, required: false },
  { id: 'm-termino', groupName: 'Término (carne)', name: 'Término medio', priceCents: 0, required: true },
]
export const COMBOS: Combo[] = [
  { id: 'cmb-comida', name: 'Comida corrida', priceCents: $(140), itemIds: ['p-quesa', 'p-mole', 'p-agua'] },
]
export const PRICE_LISTS: PriceList[] = [
  { id: 'pl-base', name: 'Precios base', active: true, overrides: {} },
  { id: 'pl-happyhour', name: 'Happy hour', active: false, overrides: { 'p-agua': $(30), 'p-refresco': $(28) } },
]

export const FLOOR_SECTIONS: FloorSection[] = [
  { id: 'sec-sala', name: 'Salón' },
  { id: 'sec-barra', name: 'Barra' },
  { id: 'sec-terraza', name: 'Terraza' },
]
export const FLOOR_TABLES: FloorTable[] = [
  { id: 't1', code: 'M1', capacity: 2, posX: 40, posY: 40, widthPx: 90, heightPx: 90, shape: 'ROUND', sectionId: 'sec-sala' },
  { id: 't2', code: 'M2', capacity: 4, posX: 170, posY: 40, widthPx: 130, heightPx: 90, shape: 'RECT', sectionId: 'sec-sala' },
  { id: 't3', code: 'M3', capacity: 4, posX: 340, posY: 40, widthPx: 130, heightPx: 90, shape: 'RECT', sectionId: 'sec-sala' },
  { id: 't4', code: 'M4', capacity: 6, posX: 40, posY: 180, widthPx: 180, heightPx: 100, shape: 'RECT', sectionId: 'sec-sala' },
  { id: 'b1', code: 'B1', capacity: 1, posX: 280, posY: 200, widthPx: 70, heightPx: 70, shape: 'ROUND', sectionId: 'sec-barra' },
  { id: 'b2', code: 'B2', capacity: 1, posX: 370, posY: 200, widthPx: 70, heightPx: 70, shape: 'ROUND', sectionId: 'sec-barra' },
  { id: 't5', code: 'T1', capacity: 4, posX: 40, posY: 320, widthPx: 130, heightPx: 90, shape: 'RECT', sectionId: 'sec-terraza' },
]

export const USERS: AdminUser[] = [
  { id: 'u-owner', fullName: 'Dueño Demo', email: 'dueno@nintaigo.demo', role: 'owner', pinId: '1000', active: true },
  { id: 'u-mgr', fullName: 'Ana Gerente', email: 'ana@nintaigo.demo', role: 'manager', pinId: '2001', active: true },
  { id: 'u-mesero1', fullName: 'Beto Mesero', email: 'beto@nintaigo.demo', role: 'waiter', pinId: '3001', active: true },
  { id: 'u-coci', fullName: 'Cocina', email: 'cocina@nintaigo.demo', role: 'kitchen', pinId: '4001', active: true },
  { id: 'u-mesero2', fullName: 'Dora Mesera', email: 'dora@nintaigo.demo', role: 'waiter', pinId: null, active: false },
]
export const ROLES = ['owner', 'manager', 'waiter', 'cashier', 'kitchen'] as const
export const PRINTERS: Printer[] = [
  { id: 'pr-caja', name: 'Caja principal', ip: '192.168.0.50', station: 'Caja', kind: 'RECEIPT', online: true },
  { id: 'pr-cocina', name: 'Cocina caliente', ip: '192.168.0.51', station: 'Cocina', kind: 'KITCHEN', online: true },
  { id: 'pr-barra', name: 'Barra', ip: '192.168.0.52', station: 'Barra', kind: 'KITCHEN', online: false },
]
export const KDS_STATIONS: KdsStation[] = [
  { id: 'kds-cocina', name: 'Cocina', categories: ['cat-entradas', 'cat-fuertes'] },
  { id: 'kds-barra', name: 'Barra', categories: ['cat-bebidas'] },
]

export const FISCAL_SETTINGS: FiscalSettings = {
  rfc: 'SCO210815AB3', legalName: 'SAVIs Coffee SA de CV', regimeCode: '626', regimeName: 'Régimen Simplificado de Confianza (RESICO)',
  csdUploaded: false, csdExpiresAt: null,
  folioSeries: 'A', folioNext: 1,
  businessHours: [
    { day: 'Lun–Vie', open: '08:00', close: '22:00' },
    { day: 'Sáb', open: '09:00', close: '23:00' },
    { day: 'Dom', open: '09:00', close: '18:00' },
  ],
  receiptHeader: 'SAVIs Coffee',
  receiptFooter: '¡Gracias por tu visita! · Factura tu consumo en saviscoffee.mx/factura',
}

export const REPORTS: ReportSummary[] = [
  { id: 'rep-ventas-dia', name: 'Ventas del día', period: 'Hoy', updatedAt: new Date().toISOString() },
  { id: 'rep-ventas-mes', name: 'Ventas del mes', period: 'Mes en curso', updatedAt: new Date().toISOString() },
  { id: 'rep-productos', name: 'Productos más vendidos', period: 'Últimos 30 días', updatedAt: new Date().toISOString() },
  { id: 'rep-meseros', name: 'Desempeño por mesero', period: 'Últimos 30 días', updatedAt: new Date().toISOString() },
  { id: 'rep-cortes', name: 'Cortes de caja', period: 'Últimos 30 días', updatedAt: new Date().toISOString() },
]

export const INVOICES: Invoice[] = [
  { id: 'inv-1', folio: 'A-1021', customer: 'Comercializadora Sol SA', rfc: 'CSO980101AB1', totalCents: $(4350), status: 'STAMPED', date: '2026-05-09', uuid: 'A1B2C3D4-0000-0000-0000-000000000001' },
  { id: 'inv-2', folio: 'A-1022', customer: 'Público en general', rfc: 'XAXX010101000', totalCents: $(890), status: 'STAMPED', date: '2026-05-10', uuid: 'A1B2C3D4-0000-0000-0000-000000000002' },
  { id: 'inv-3', folio: 'A-1023', customer: 'Juan Pérez', rfc: 'PEPJ800101HDF', totalCents: $(265), status: 'PENDING', date: '2026-05-11', uuid: '' },
  { id: 'inv-glob-abr', folio: 'G-2026-04', customer: 'Factura global abril', rfc: 'XAXX010101000', totalCents: $(38420), status: 'STAMPED', date: '2026-05-01', uuid: 'A1B2C3D4-0000-0000-0000-0000000000G4' },
]
export const FISCAL_CUSTOMERS: FiscalCustomer[] = [
  { id: 'fc-1', name: 'Comercializadora Sol SA', rfc: 'CSO980101AB1', email: 'cobros@sol.mx', regimeCode: '601', usoCfdi: 'G03' },
  { id: 'fc-2', name: 'Juan Pérez', rfc: 'PEPJ800101HDF', email: 'juan@correo.mx', regimeCode: '612', usoCfdi: 'G03' },
]
export const INVENTORY: InventoryItem[] = [
  { id: 'i-aguacate', name: 'Aguacate', unit: 'kg', onHand: 24, reorderLevel: 10, status: 'OK' },
  { id: 'i-tortilla', name: 'Tortilla', unit: 'kg', onHand: 6, reorderLevel: 8, status: 'LOW' },
  { id: 'i-pollo', name: 'Pechuga de pollo', unit: 'kg', onHand: 0, reorderLevel: 5, status: 'OUT' },
  { id: 'i-jamaica', name: 'Flor de jamaica', unit: 'kg', onHand: 3.5, reorderLevel: 2, status: 'OK' },
]

export const TENANTS: TenantAccount[] = [
  { id: 'tnt_demo', name: 'Nintai Demo', legalName: 'Nintai Demo SA de CV', plan: 'restaurant-pro', status: 'ACTIVE', mode: 'SANDBOX', createdAt: '2025-11-02', ownerEmail: 'dueno@nintaigo.demo' },
  { id: 'tnt_taqueria', name: 'Taquería El Buen Sabor', legalName: 'El Buen Sabor SC', plan: 'restaurant-lite', status: 'ACTIVE', mode: 'LIVE', createdAt: '2026-01-15', ownerEmail: 'dueno@elbuensabor.mx' },
  { id: 'tnt_cafe', name: 'Café Aurora', legalName: 'Aurora Café SA de CV', plan: 'restaurant-pro', status: 'TRIAL', mode: 'SANDBOX', createdAt: '2026-04-28', ownerEmail: 'hola@cafeaurora.mx' },
  { id: 'tnt_cantina', name: 'Cantina La Esquina', legalName: 'La Esquina SC', plan: 'restaurant-lite', status: 'SUSPENDED', mode: 'LIVE', createdAt: '2025-09-10', ownerEmail: 'admin@laesquina.mx' },
]

// Onboarding step 4 default ingredients (the user edits the costs).
export const ONBOARDING_INGREDIENTS: Ingredient[] = [
  { id: 'ing-tortilla', name: 'Tortilla', unit: 'pieza', costCents: 60 },
  { id: 'ing-carne', name: 'Carne de pastor', unit: 'kg', costCents: 18000 },
  { id: 'ing-cebolla', name: 'Cebolla', unit: 'kg', costCents: 2500 },
  { id: 'ing-cilantro', name: 'Cilantro', unit: 'kg', costCents: 3500 },
]
// Onboarding step 5 default recipe (2 tortillas, 50 g carne, 10 g cebolla, 3 g cilantro per portion).
export const ONBOARDING_RECIPE: RecipeLine[] = [
  { ingredientId: 'ing-tortilla', qtyPerPortion: 2 },
  { ingredientId: 'ing-carne', qtyPerPortion: 0.05 },
  { ingredientId: 'ing-cebolla', qtyPerPortion: 0.01 },
  { ingredientId: 'ing-cilantro', qtyPerPortion: 0.003 },
]

export { $ as mxnCents }
